
import { Component, Vue, Watch } from 'vue-property-decorator';
import vSelect from 'vue-select';
import LocationsMap from '@/components/gis/LocationsMap.vue';
import LoadingSkeleton from '@/components/commonui/LoadingSkeleton.vue';
import toDateFormatter from '@/filters/todate.formatter';
import toHoursMinutesFormatter from '@/filters/tohoursminutes.formatter';
import {
  Client,
  IDocumentModel,
  IInvoiceModel,
  IJobItemActivityModel,
  IJobItemModel,
  IJobItemScheduleModel,
  IJobModel,
  IJobNoteModel,
  ILocationModel,
  Status as JobItemModelStatus
} from '@/services/api.service';
import * as DateUtils from '@/utilities/date.utils';
import JobCard from '@/views/job/_shared/JobCard.vue';
import UploadDocument from '@/views/job/document/UploadDocument.vue';
import JobDocumentList from './components/JobDocumentList.vue';
import JobInvoiceList from './components/JobInvoiceList.vue';
import JobItemActivityList from './components/JobItemActivityList.vue';
import JobItemCard from './components/JobItemCard.vue';
import JobItemListRow from './components/JobItemListRow.vue';
import JobNoteCard from './components/JobNoteCard.vue';
import AddJobItem from './processes/AddJobItem.vue';
import AddJobItemActivity from './processes/AddJobItemActivity.vue';
import AddJobItemInvoice from './processes/AddJobItemInvoice.vue';
import AddJobNote from './processes/AddJobNote.vue';
import JobEdit from './processes/JobEdit.vue';
import JobItemEdit from './processes/JobItemEdit.vue';
import JobNoteEdit from './processes/JobNoteEdit.vue';
import RepeatJobItem from './processes/RepeatJobItem.vue';
import ScheduleJobItem from './processes/ScheduleJobItem.vue';

  export const NoFolder: string = '(No Folder)';

  @Component({
    components: {
      vSelect,
      JobEdit,
      JobCard,
      JobItemCard,
      JobItemEdit,
      JobNoteCard,
      JobNoteEdit,
      AddJobNote,
      LocationsMap,
      LoadingSkeleton,
      ScheduleJobItem,
      AddJobItem,
      AddJobItemActivity,
      RepeatJobItem,
      JobDocumentList,
      JobItemActivityList,
      UploadDocument,
      AddJobItemInvoice,
      JobInvoiceList,
      JobItemListRow
    },
    filters: {
      toDate: toDateFormatter,
      toHoursMinutes: toHoursMinutesFormatter
    }
  })
  export default class JobView extends Vue {
    private gid: string = '';
    private clientName: string = '';
    private jobNumber: string = '';
    private jobIsQuotedProject: boolean = false;
    private jobType: number = 0;
    private job: IJobModel | null = null;
    private jobItems: IJobItemModel[] = [];
    private filteredJobItems: IJobItemModel[] = [];
    private nextJobItem: IJobItemModel | null = null;
    private showCompletedJobItems: boolean = false;
    private showCancelledJobItems: boolean = false;

    private jobNotes: IJobNoteModel[] = [];
    private lastTwoJobNotes: IJobNoteModel[] = [];

    private locations: ILocationModel[] = [];
    private activities: IJobItemActivityModel[] = [];
    private documents: IDocumentModel[] = [];
    private documentFolders: string[] = [];
    private invoices: IInvoiceModel[] = [];

    private jobLoaded: boolean = false;
    private jobItemsLoaded: boolean = false;
    private locationsLoaded: boolean = false;
    private documentsLoaded: boolean = false;
    private invoicesLoaded: boolean = false;
    private jobNotesLoaded: boolean = false;
    private activitiesLoaded: boolean = false;

    private jobItemInEdit: IJobItemModel | null = null;
    private jobNoteInEdit: IJobNoteModel | null = null;
    private jobNoteInDeleteRequested: IJobNoteModel | null = null;
    private jobItemScheduleInEdit: IJobItemScheduleModel | null = null;
    private jobDocumentInDeleteRequested: IDocumentModel | null = null;
    private jobItemActivityInDeleteRequested: IJobItemActivityModel | null = null;

    private jobItemsCount: number = 0;
    private completedJobItemsCount: number = 0;
    private cancelledJobItemsCount: number = 0;
    private activitiesCount: number = 0;
    private notesCount: number = 0;
    private documentsCount: number = 0;
    private invoicesCount: number = 0;

    private locationInEdit: ILocationModel | null = null;

    private get uninvoicedActivitiesCount(): number {
      return this.activities.filter(a => !a.isInvoiced).length;
    }

    protected mounted() {
      // get the networks
      this.gid = this.$route.params.gid;

      // @ts-ignore
      this.$Progress.start();
      this.fetchJobData(this.gid);
    }

    private async fetchJobData(jobgid: string) {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      const [theJob, theJobItems, theLocations, theActivities] = await Promise.all([
        clientService.getJob(jobgid),
        clientService.getJobItems(jobgid),
        clientService.getJobLocations(jobgid),
        this.fetchActivities(this.gid)
      ]);

      this.job = theJob;
      this.jobNumber = theJob.jobNumber || '';
      this.jobType = theJob.jobType || 0;
      this.jobIsQuotedProject = theJob.isQuotedProject || false;
      this.jobItems = theJobItems;
      this.onFilterJobItems();
      this.locations = theLocations;

      this.jobLoaded = true;
      this.jobItemsLoaded = true;
      this.locationsLoaded = true;

      this.findNextJobItem();
      console.log('Returned documents:', this.documents);

      // this.fetchActivities(this.gid);
      this.fetchNotes(this.gid);
      this.fetchDocuments(this.gid);
      this.fetchInvoices(this.gid);
    }

    private onFilterJobItems() {
      // Only inlcude completed jobs in the completed list
      // if there are no activities pending invoicing

      this.filteredJobItems = this.jobItems.filter(
        ji =>
          (this.showCompletedJobItems || (ji.status !== JobItemModelStatus._4 || this.hasActivityPendingInvoicing(ji.gid || ''))) &&
          (this.showCancelledJobItems || ji.status !== JobItemModelStatus._5)

      );
      this.cancelledJobItemsCount = this.jobItems.filter(ji => ji.status === JobItemModelStatus._5).length;
      this.completedJobItemsCount = this.jobItems.filter(ji => ji.status === JobItemModelStatus._4 && !this.hasActivityPendingInvoicing(ji.gid || '')).length;
      this.jobItemsCount = this.jobItems.length;
    }
    private async fetchNotes(jobgid: string) {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      const theJobNotes = await clientService.getJobNotesForJob(jobgid);

      // sort them
      const sortedData = theJobNotes.sort((d1, d2) =>
        DateUtils.dateCompare(d1.createdDateTime || new Date(), d2.createdDateTime || new Date())
      );
      this.jobNotes = sortedData.reverse();
      this.jobNotesLoaded = true;
      this.notesCount = this.jobNotes.length;
      this.findLastTwoNotes();
      this.updateProgress();
    }

    private hasActivityPendingInvoicing(jobItemGid: string): boolean {
      if (this.activities && this.activities.length > 0 && jobItemGid !== '') {
        return this.activities.some((a: IJobItemActivityModel) => a.jobItemGid === jobItemGid && !a.isInvoiced && a.invoiceType === 0); // ActivityInvoiceType.Normal
      }
      return false;
    }

    private async fetchDocuments(jobgid: string) {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      const theJobDocuments = await clientService.getJobDocuments(jobgid);

      // sort them
      const sortedData = theJobDocuments.sort((d1, d2) =>
        DateUtils.dateCompare(d1.uploadedDateTime || new Date(), d2.uploadedDateTime || new Date())
      );
      this.documents = sortedData;
      this.documentsLoaded = true;
      this.documentsCount = this.documents.length;

      // Build a list of folders
      if (this.documents) {
        // Get the folder names from the documents
        const names = this.documents.map((d: IDocumentModel) => d.folderName || '');
        // See if there are non-foldered items
        const emptyNames = names.filter(n => n === '');
        let hasEmpty: boolean = false;
        if (emptyNames && emptyNames.length > 0) {
          hasEmpty = true;
        }
        // Use Set to get the unique set, then sort
        this.documentFolders = Array.from(new Set(names.filter(n => n !== '')));
        this.documentFolders.sort();
        // Insert the No Folder option
        if (hasEmpty) {
          this.documentFolders.unshift(NoFolder);
        }
      }

      this.updateProgress();
    }

    private async fetchInvoices(jobgid: string) {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      const theJobInvoices = await clientService.getJobInvoices(jobgid);

      // sort them
      const sortedData = theJobInvoices.sort((d1, d2) =>
        DateUtils.dateCompare(d1.invoiceDate || new Date(), d2.invoiceDate || new Date())
      );
      this.invoices = sortedData;
      this.invoicesLoaded = true;
      this.invoicesCount = this.invoices.length;
      this.updateProgress();
    }

    private async fetchActivities(jobgid: string) {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      const theActivities = await clientService.getJobActivities(jobgid);
      this.activities = theActivities;
      this.activitiesLoaded = true;
      this.activitiesCount = this.activities.length;
      this.updateProgress();
    }

    private updateProgress() {
      if (this.activitiesLoaded && this.jobNotesLoaded && this.documentsLoaded && this.invoicesLoaded) {
        // @ts-ignore
        this.$Progress.finish();
      }
    }

    private findNextJobItem() {
      // Work out what the next job item is
      // based on scheduled date etc
      const pendingJobs = this.jobItems.filter(
        ji => ji.status === JobItemModelStatus._1 || ji.status === JobItemModelStatus._0
      );
      if (pendingJobs && pendingJobs.length > 0) {
        // We might have multiple jobs here (some pending, some scheduled)
        // and our interest is primarily scheduled jobs
        const nextScheduled = pendingJobs.find(j => j.status === JobItemModelStatus._1);
        if (nextScheduled) {
          this.nextJobItem = nextScheduled;
        } else {
          this.nextJobItem = pendingJobs[0];
        }
      }
    }

    private findLastTwoNotes() {
      this.lastTwoJobNotes = [];
      if (this.jobNotes.length > 0) {
        if (this.jobNotes.length > 1) {
          this.lastTwoJobNotes = [this.jobNotes[0], this.jobNotes[1]];
        } else {
          // Only one note to get
          this.lastTwoJobNotes = [this.jobNotes[0]];
        }
      }
    }

    private get hasAdminTask(): boolean {
      if (this.jobItemInEdit) {
        let hasAdminTask = false;
        let jobInEditGid: string | undefined;
        if (this.jobItemInEdit) {
          jobInEditGid = this.jobItemInEdit.gid;
        }
        this.activities.forEach((activity) => {
          if ((activity.jobItemGid === jobInEditGid) && activity.isAdminActivity) { hasAdminTask = true; }
        });
        return hasAdminTask;
      } else {
        return false;
      }
    }

    private getActivitiesForJobItem(jobItemGid: string): IJobItemActivityModel[] {
      return this.activities.filter(a => a.jobItemGid === jobItemGid);
    }

    @Watch('showCompletedJobItems')
    private onShowCompletedJobItemsChanged() {
      this.onFilterJobItems();
    }
    @Watch('showCancelledJobItems')
    private onShowCancelledJobItemsChanged() {
      this.onFilterJobItems();
    }
    private onJobEditRequested() {
      console.log('EVENT:Edit Job requested');
      // this.jobItemInEdit = value;
      this.$modal.show('modal-job-edit');
    }

    private onJobUpdated(job: IJobModel) {
      console.log('Received job-updated event');
      console.log(job);
      if (this.job) {
        Object.assign(this.job, job);
      }
    }

    private cancelCreateJob() {
      this.$router.push({ path: '/jobs' });
    }

    private onUploadFileRequested() {
      this.$modal.show('modal-jobdocument-upload');
    }
    private onJobItemAddRequested() {
      this.$modal.show('modal-jobitem-create');
    }

    private onJobItemInvoiceCreateRequested(value: IJobItemModel) {
      console.log('EVENT:Create JobItem Invoice requested for ' + value.gid);
      this.jobItemInEdit = value;
      this.$modal.show('modal-jobiteminvoice-create');
    }
    private onJobNoteAddRequested() {
      this.$modal.show('modal-jobnote-create');
    }

    private onJobNoteDeleteRequested(value: IJobNoteModel) {
      console.log('EVENT:Delete job note requested ');
      this.jobNoteInDeleteRequested = value;
      // @ts-ignore
      this.$refs.deletejobnoteprompt.show();
    }

    private onContinueDeleteJobNote() {
      console.log('EVENT:Confirmed delete job note');
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      if (this.jobNoteInDeleteRequested && this.jobNoteInDeleteRequested.gid) {
        // @ts-ignore
        this.$Progress.start();
        const employees = clientService
          .deleteJobNote(this.gid, this.jobNoteInDeleteRequested.gid)
          .then(data => {
            // got the deleted employee, so remove from the list
            // and then pop a message
            // @ts-ignore
            this.$Progress.finish();
            // Remove the employee from the list
            const reducedJobNotes = this.jobNotes.filter(e => e.gid !== data.gid);
            console.log(reducedJobNotes);
            this.jobNotes = reducedJobNotes;
            this.notesCount = this.jobNotes.length;
            this.findLastTwoNotes();
            // @ts-ignore
            this.$awn.success('Job note deleted successfully');
            this.$emit('jobnote-deleted', data);
          })
          .catch(reason => {
            // Pop the error message
            const errMsg = 'An error occurred when deleting the job note: <br/><br/>' + reason;
            // @ts-ignore
            this.$Progress.finish();
            // @ts-ignore
            this.$awn.alert(errMsg);
          });
      }
    }
    private onJobItemActivityAddRequested(value: IJobItemModel) {
      console.log('EVENT:Add Activity requested ');
      this.jobItemInEdit = value;
      this.$modal.show('modal-jobitemactivity-create');
    }

    private onJobItemEditRequested(value: IJobItemModel) {
      console.log('EVENT:Edit JobItem requested for ' + value.gid);
      this.jobItemInEdit = value;
      this.$modal.show('modal-jobitem-edit');
    }

    private onJobNoteEditRequested(value: IJobNoteModel) {
      console.log('EVENT:Edit JobNote requested for ' + value.gid);
      this.jobNoteInEdit = value;
      this.$modal.show('modal-jobnote-edit');
    }

    private onLocationEditRequested(value: ILocationModel) {
      console.log('EVENT:Edit Location requested for ' + value.gid);
      this.locationInEdit = value;
      this.$modal.show('modal-location-edit');
    }

    private onJobItemScheduleRequested(value: IJobItemModel) {
      console.log('EVENT:Schedule JobItem requested for ' + value.gid);
      this.jobItemInEdit = value;
      // Get the schedule item from the job item
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.getCurrentJobItemSchedule(this.gid, this.jobItemInEdit.gid!).then(data => {
        this.jobItemScheduleInEdit = data;
        this.$modal.show('modal-jobitem-schedule');
      });
    }

    private onJobItemRepeatRequested(value: IJobItemModel) {
      console.log('EVENT:Repeat JobItem requested for ' + value.gid);
      this.jobItemInEdit = value;
      this.$modal.show('modal-jobitem-repeat');
    }

    private onJobItemTransitionRequested(value: IJobItemModel) {
      console.log('EVENT:Transition JobItem requested for ' + value.gid);
      this.jobItemInEdit = value;
    }

    private onJobDocumentDeleteRequested(value: IDocumentModel) {
      console.log('EVENT:Delete job document requested ');
      this.jobDocumentInDeleteRequested = value;
      // @ts-ignore
      this.$refs.deletejobdocumentprompt.show();
    }

    private onContinueDeleteJobDocument() {
      console.log('EVENT:Confirmed delete job document');
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      if (this.jobDocumentInDeleteRequested && this.jobDocumentInDeleteRequested.gid) {
        // @ts-ignore
        this.$Progress.start();
        clientService
          .deleteDocument(this.jobDocumentInDeleteRequested.gid)
          .then(data => {
            // got the deleted document, so remove from the list
            // and then pop a message
            // @ts-ignore
            this.$Progress.finish();
            // Remove the employee from the list
            const reducedDocuments = this.documents.filter(e => e.gid !== data.gid);
            console.log(reducedDocuments);
            this.documents = reducedDocuments;
            this.documentsCount = this.documents.length;
            // @ts-ignore
            this.$awn.success('Job document deleted successfully');
            this.$emit('jobdocument-deleted', data);
          })
          .catch(reason => {
            // Pop the error message
            const errMsg = 'An error occurred when deleting the job document: <br/><br/>' + reason;
            // @ts-ignore
            this.$Progress.finish();
            // @ts-ignore
            this.$awn.alert(errMsg);
          });
      }
    }
    // Event Sinks
    private onJobItemCreated(jobItem: IJobItemModel) {
      // make suer this job item is for this job
      if (jobItem.jobGid === this.gid) {
        this.jobItems.push(jobItem);
        this.jobItemsCount = this.jobItems.length;
        this.onFilterJobItems();
        this.findNextJobItem();
      }
    }
    private onJobItemUpdated(jobItem: IJobItemModel) {
      console.log('Received job-item-updated event');
      const theJobItem = this.jobItems.find(item => item.gid === jobItem.gid);
      if (theJobItem !== undefined) {
        Object.assign(theJobItem, jobItem);
      }
      this.onFilterJobItems();
      this.findNextJobItem();
    }

    private onJobNoteCreated(jobNote: IJobNoteModel) {
      // make suer this job item is for this job
      if (jobNote.jobGid === this.gid) {
        const theJobNotes: IJobNoteModel[] = this.jobNotes.map(n => n);
        theJobNotes.push(jobNote);
        const sortedData = theJobNotes.sort((d1, d2) =>
          DateUtils.dateCompare(d1.createdDateTime || new Date(), d2.createdDateTime || new Date())
        );
        this.jobNotes = sortedData.reverse();
        // this.jobNotes.push(jobNote);
        this.notesCount = this.jobNotes.length;
        this.findLastTwoNotes();
      }
    }
    private onJobNoteUpdated(jobNote: IJobNoteModel) {
      console.log('Received job-item-updated event');
      const theJobNote = this.jobNotes.find(item => item.gid === jobNote.gid);
      if (theJobNote !== undefined) {
        Object.assign(theJobNote, jobNote);
      }
    }

    private onJobItemScheduled(jobItemSchedule: IJobItemScheduleModel) {
      console.log('Received job-item-scheduled event');
    }

    private onJobItemsRepeated() {
      console.log('Received job-items-repeated event');
      // Refresh job items list
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.getJobItems(this.gid).then(jobItems => {
        this.jobItems = jobItems;
        this.jobItemsCount = this.jobItems.length;
        this.onFilterJobItems();
        this.findNextJobItem();
      });
    }

    private onJobItemActivityCreated(props: { jobItemActivity: IJobItemActivityModel, createInvoice: boolean }) {
      console.log('Received job-item-activity-created event');

      // Reload the activities
      this.fetchActivities(this.gid).then(() => {
        if (props.createInvoice) {
          this.jobItemInEdit = this.jobItems.find((jobItem) => jobItem.gid === props.jobItemActivity.jobItemGid) || this.jobItems[0];
          this.$modal.show('modal-jobiteminvoice-create');
        }
      });
    }

    private onJobInvoiceDeleteRequested(value: IInvoiceModel) {
      console.log('EVENT:Delete job invoice requested - NOT YET IMPLEMENTED');
    }

    private onJobItemActivityDeleteRequested(value: IJobItemActivityModel) {
      console.log('EVENT:Delete job item activity requested ');
      this.jobItemActivityInDeleteRequested = value;
      // @ts-ignore
      this.$refs.deletejobitemactivityprompt.show();
    }

    private onContinueDeleteJobItemActivity() {
      console.log('EVENT:Confirmed delete job item activity');
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      if (
        this.jobItemActivityInDeleteRequested &&
        this.jobItemActivityInDeleteRequested.gid &&
        this.jobItemActivityInDeleteRequested.jobItemGid
      ) {
        // @ts-ignore
        this.$Progress.start();
        clientService
          .deleteJobItemActivity(
            this.gid,
            this.jobItemActivityInDeleteRequested.jobItemGid,
            this.jobItemActivityInDeleteRequested.gid
          )
          .then(data => {
            // got the deleted employee, so remove from the list
            // and then pop a message
            // @ts-ignore
            this.$Progress.finish();
            // Remove the employee from the list
            const reducedActivities = this.activities.filter(e => e.gid !== data.gid);
            console.log(reducedActivities);
            this.activities = reducedActivities;
            this.activitiesCount = this.activities.length;
            // @ts-ignore
            this.$awn.success('Job activity deleted successfully');
            this.$emit('jobitemactivity-deleted', data);
          })
          .catch(reason => {
            // Pop the error message
            const errMsg = 'An error occurred when deleting the job activity: <br/><br/>' + reason;
            // @ts-ignore
            this.$Progress.finish();
            // @ts-ignore
            this.$awn.alert(errMsg);
          });
      }
    }
    private onJobDocumentUploaded(document: IDocumentModel) {
      console.log('Received job-document-uploaded event');
      if (document.jobGid === this.gid) {
        this.documents.push(document);
        this.documentsCount = this.documents.length;
        // see if the folder name is new
        if (document.folderName && document.folderName !== '') {
          const currentFolder = this.documentFolders.find(df => df === document.folderName);
          if (!currentFolder) {
            this.documentFolders.push(document.folderName);
            this.documentFolders.sort();
          }
        } else {
          // as no folder name is specified ensure the no folder entry exists
          const NoFolderEntryExists = this.documentFolders.some((folder) => folder === NoFolder);
          if (!NoFolderEntryExists) {
            this.documentFolders.push(NoFolder);
          }
        }
      }
    }

    private onJobItemInvoiceCreated(props: { jobInvoice: IInvoiceModel, invoicedActivites: string[]}) {
      // make sure this job item is for this job
      if (props.jobInvoice.jobGid === this.gid) {
        this.invoices.push(props.jobInvoice);
        this.invoicesCount = this.invoices.length;
        // Update activities that have been invoiced
        props.invoicedActivites.forEach(activityGid => {
          const matchingActivity = this.activities.find((activity) => activity.gid === activityGid);
          if (matchingActivity) {
            matchingActivity.isInvoiced = true;
          }
        });
      }
    }
  }
